import { SoftgardenApiError, logSoftgardenDebug } from './api.js';
import {
	clearStoredCredential,
	readStoredCredential,
	writeStoredCredential,
} from './credentials-store.js';
import {
	clearResetAuthTokenFromUrl,
	readResetAuthToken,
	validateSoftgardenPassword,
} from './password.js';
import { collectAnswers, renderQuestions } from './questions.js';

const SESSION_KEY = 'sg_apply_session';

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const debugFlowError = (context, error) => {
	if (error instanceof SoftgardenApiError) {
		logSoftgardenDebug(context, {
			status: error.status,
			code: error.code,
			message: error.message,
			body: error.body,
		});
		return;
	}

	logSoftgardenDebug(context, {
		message: error?.message || String(error),
		error,
	});
};

const collectErrorParts = (error) => {
	if (!(error instanceof SoftgardenApiError) && !(error instanceof Error)) {
		return String(error || '');
	}

	return [
		error.code,
		error.message,
		typeof error.body === 'string' ? error.body : '',
		error.body?.code,
		error.body?.message,
		error.body?.error,
		error.body?.error_description,
	].filter(Boolean).join(' ');
};

const isExistingAccountError = (error) => {
	if (!(error instanceof SoftgardenApiError)) {
		return false;
	}

	return /username_already_exists|mail_address_already_exists|email_address_already_exists/i
		.test(collectErrorParts(error));
};

const isAlreadyAppliedError = (error) => {
	if (!(error instanceof SoftgardenApiError)) {
		return false;
	}

	const parts = collectErrorParts(error);

	// Softgarden createDraft: { code: "project_not_applicable", message: "Application for this project already exists…" }
	return /application for this project already exists|already_applied|application_already_exists|duplicate_application/i
		.test(parts);
};

const isUploadSizeError = (error) => {
	if (!(error instanceof SoftgardenApiError) && !(error instanceof Error)) {
		return false;
	}

	const parts = collectErrorParts(error);
	if (/upload_too_large|upload_partial/i.test(parts)) {
		return true;
	}

	// Nur echter HTTP-415, nicht zufällige "415" in IDs/Texten
	return error instanceof SoftgardenApiError
		&& error.status === 415;
};

const guessMimeFromName = (filename = '') => {
	const ext = filename.split('.').pop()?.toLowerCase();
	switch (ext) {
		case 'pdf':
			return 'application/pdf';
		case 'doc':
			return 'application/msword';
		case 'docx':
			return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
		default:
			return '';
	}
};

const resolveUserMessage = (error, messages, fallbackKey = 'genericError') => {
	if (!(error instanceof SoftgardenApiError) && !(error instanceof Error)) {
		return messages[fallbackKey] || messages.genericError;
	}

	if (isAlreadyAppliedError(error)) {
		return messages.alreadyApplied;
	}

	const parts = collectErrorParts(error);

	// PHP/Proxy: Upload am Server abgelehnt – nicht mit Client-10-MB-Text vermischen
	if (/upload_too_large/i.test(parts)) {
		return messages.uploadServerLimit || messages.fileTooLarge || messages.uploadError;
	}

	if (
		isUploadSizeError(error)
		|| /upload_failed|upload_missing|upload_invalid|upload_partial|upload_no_file/i.test(parts)
		|| (error instanceof SoftgardenApiError && error.status === 415)
	) {
		return messages.uploadError || messages.submitError;
	}

	if (error instanceof SoftgardenApiError && (error.status === 401 || /session|unauthorized|invalid_token|access_denied/i.test(parts))) {
		return messages.authRequired;
	}

	const raw = (error.message || '').trim();
	const knownKeys = Object.values(messages).filter(Boolean);

	if (raw && knownKeys.includes(raw)) {
		return raw;
	}

	return messages[fallbackKey] || messages.genericError;
};

const readSession = (jobId) => {
	try {
		const raw = sessionStorage.getItem(SESSION_KEY);
		if (!raw) return null;
		const data = JSON.parse(raw);
		return data.jobId === jobId ? data : null;
	} catch {
		return null;
	}
};

const writeSession = (payload) => {
	sessionStorage.setItem(SESSION_KEY, JSON.stringify(payload));
};

const clearSession = () => {
	sessionStorage.removeItem(SESSION_KEY);
};

const generatePassword = () => {
	const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*';
	let password = 'Aa1!';
	for (let i = 0; i < 12; i += 1) {
		password += chars.charAt(Math.floor(Math.random() * chars.length));
	}
	return password;
};

const collectProfile = (form, root, config) => {
	const formData = new FormData(form);
	const locale = config.locale;

	return {
		firstname: formData.get('firstname')?.toString().trim() || '',
		lastname: formData.get('lastname')?.toString().trim() || '',
		sex: formData.get('salutation')?.toString() || '0',
		email: formData.get('email')?.toString().trim() || '',
		locale,
		street: formData.get('street')?.toString().trim() || '',
		zip: formData.get('zip')?.toString().trim() || '',
		city: formData.get('city')?.toString().trim() || '',
		phone: formData.get('phone')?.toString().trim() || '',
		coverLetterText: formData.get('coverLetterText')?.toString().trim() || '',
		answers: collectAnswers(root.querySelector('[data-list="questions"]')),
		imported: 'NONE',
		...(config.applicationSourceId ? {
			applicationSource: { id: config.applicationSourceId },
		} : {}),
	};
};

const uploadFile = async (api, applicationId, file) => {
	const formData = new FormData();
	const mime = file.type || guessMimeFromName(file.name);
	const payload = mime && file.type !== mime
		? new File([file], file.name || 'upload', { type: mime })
		: file;
	formData.append('file', payload, payload.name || 'upload');

	return api.postForm(
		`/api/rest/v3/frontend/attachments/?applicationId=${encodeURIComponent(applicationId)}`,
		formData,
		{ auth: 'user' },
	);
};

export const initSoftgardenFlow = (root, api, messages = {}) => {
	const config = {
		jobId: root.dataset.jobId,
		locale: root.dataset.locale || 'de',
		apiLocale: root.dataset.apiLocale || 'DE',
		applicationSourceId: root.dataset.applicationSourceId || '',
		maxFileSize: (() => {
			const parsed = Number.parseInt(root.dataset.maxFileSize || '', 10);
			// Fallback 10 MB; Werte < 1 MB sind vermutlich Fehlkonfiguration
			return Number.isFinite(parsed) && parsed >= 1048576 ? parsed : 10485760;
		})(),
	};

	const ui = {
		status: root.querySelector('.apply-form__status'),
		loading: root.querySelector('.apply-form__loading'),
		loadingText: root.querySelector('[data-loading-text]'),
		main: root.querySelector('.apply-form__main'),
		success: root.querySelector('.apply-form__success'),
		existing: root.querySelector('.apply-form__existing-account'),
		existingHeading: root.querySelector('#sg-existing-account-heading'),
		reset: root.querySelector('.apply-form__reset-password'),
		configError: root.querySelector('.apply-form__error-config'),
		questionsFieldset: root.querySelector('#sg-questions-fieldset'),
		social: root.querySelector('.apply-form__social'),
	};

	api.userToken = null;
	let pendingApplicant = null;
	let resetAuthToken = readResetAuthToken();

	const revealFeedback = (target) => {
		if (!target) return;

		const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
		target.scrollIntoView({ behavior, block: 'center' });

		window.requestAnimationFrame(() => {
			try {
				target.focus({ preventScroll: true });
			} catch {
				target.focus();
			}
		});
	};

	const setStatus = (text, type = 'info', { focus = false } = {}) => {
		if (!ui.status) return;

		ui.status.hidden = !text;
		ui.status.textContent = text || '';
		ui.status.dataset.type = type;

		if (type === 'error') {
			ui.status.setAttribute('role', 'alert');
			ui.status.setAttribute('aria-live', 'assertive');
		} else {
			ui.status.setAttribute('role', 'status');
			ui.status.setAttribute('aria-live', 'polite');
		}

		if (text && (focus || type === 'error')) {
			revealFeedback(ui.status);
		}
	};

	const setLoading = (active, message, { focus = false } = {}) => {
		if (ui.loadingText && message) {
			ui.loadingText.textContent = message;
		} else if (ui.loadingText && !active) {
			ui.loadingText.textContent = messages.loadingDefault || ui.loadingText.textContent;
		}

		ui.loading?.toggleAttribute('hidden', !active);
		root.querySelector('[data-submit]')?.toggleAttribute('disabled', active);
		root.querySelector('.apply-form__reset [type="submit"]')?.toggleAttribute('disabled', active);
		root.querySelector('.apply-form__login [type="submit"]')?.toggleAttribute('disabled', active);

		if (active && focus) {
			revealFeedback(ui.loading);
		}
	};

	const hidePanels = () => {
		ui.main?.setAttribute('hidden', '');
		ui.existing?.setAttribute('hidden', '');
		ui.reset?.setAttribute('hidden', '');
		ui.success?.setAttribute('hidden', '');
	};

	const showMainForm = () => {
		hidePanels();
		ui.social?.removeAttribute('hidden');
		ui.main?.removeAttribute('hidden');
	};

	const showExistingAccount = (email) => {
		hidePanels();
		ui.social?.setAttribute('hidden', '');
		ui.existing?.removeAttribute('hidden');
		const emailInput = root.querySelector('#sg-login-email');
		if (emailInput && email) {
			emailInput.value = email;
		}
		revealFeedback(ui.existingHeading || ui.existing);
	};

	const showResetPassword = () => {
		hidePanels();
		ui.social?.setAttribute('hidden', '');
		ui.reset?.removeAttribute('hidden');
		revealFeedback(ui.reset);
	};

	const showSuccess = () => {
		hidePanels();
		ui.social?.setAttribute('hidden', '');
		ui.success?.removeAttribute('hidden');
		clearSession();
		revealFeedback(ui.success);
	};

	const credentialScope = api.clientId || '';

	const handleExistingAccount = (email) => {
		api.userToken = null;
		pendingApplicant = null;
		clearSession();
		showExistingAccount(email);
		// Ausführliche Copy steht im Panel – Status nicht doppeln
		setStatus('');
	};

	const tryAuthenticateWithStored = async (email) => {
		const stored = readStoredCredential(email, credentialScope);
		if (!stored) return false;

		pendingApplicant = stored;

		try {
			await ensureUserToken(
				{ username: stored.email, password: stored.password },
				{ forceRefresh: true },
			);
			return true;
		} catch {
			clearStoredCredential(email, credentialScope);
			pendingApplicant = null;
			api.userToken = null;
			return false;
		}
	};

	const ensureUserToken = async (credentials, { forceRefresh = false } = {}) => {
		if (api.userToken && !credentials && !forceRefresh) {
			return api.userToken;
		}

		const username = credentials?.username || pendingApplicant?.email;
		const password = credentials?.password || pendingApplicant?.password;

		if (!username || !password) {
			throw new SoftgardenApiError(messages.authRequired || 'Auth required', { status: 401 });
		}

		const tokenResponse = await api.requestToken(username, password);
		const accessToken = tokenResponse?.access_token || tokenResponse?.accessToken;

		if (!accessToken) {
			throw new SoftgardenApiError(messages.authRequired || 'Auth required', {
				status: 401,
				body: tokenResponse,
			});
		}

		api.userToken = accessToken;
		writeSession({
			jobId: config.jobId,
			userToken: api.userToken,
		});

		return api.userToken;
	};

	const createApplicant = async (profile) => {
		const email = profile.email.trim().toLowerCase();

		if (await tryAuthenticateWithStored(email)) {
			return api.userToken;
		}

		const password = generatePassword();
		pendingApplicant = {
			email,
			username: email,
			password,
		};

		try {
			await api.post('/api/rest/v3/frontend/applicants', {
				salutation: profile.sex,
				firstname: profile.firstname,
				lastname: profile.lastname,
				email: profile.email,
				username: profile.email,
				password,
				locale: profile.locale,
				dataPrivacyAccepted: true,
			});

			writeStoredCredential(email, password, credentialScope);
		} catch (error) {
			if (isExistingAccountError(error)) {
				if (await tryAuthenticateWithStored(email)) {
					return api.userToken;
				}
				handleExistingAccount(profile.email);
				throw error;
			}
			throw error;
		}

		return ensureUserToken();
	};

	const authenticateApplicant = async (profile) => {
		if (pendingApplicant?.password) {
			await ensureUserToken(
				{ username: pendingApplicant.email, password: pendingApplicant.password },
				{ forceRefresh: true },
			);
			return;
		}

		await createApplicant(profile);
	};

	const createDraft = async () => {
		const applicationId = await api.post(
			`/api/rest/v3/frontend/applications?jobId=${encodeURIComponent(config.jobId)}`,
			undefined,
			{ auth: 'user' },
		);

		return typeof applicationId === 'string' ? applicationId.trim() : applicationId?.applicationId;
	};

	const saveProfile = async (applicationId, profile) => {
		await api.post(`/api/rest/v3/frontend/applications/${applicationId}`, profile, { auth: 'user' });
	};

	const submitApplication = async (applicationId) => {
		await api.post(`/api/rest/v3/frontend/applications/${applicationId}/submit`, undefined, { auth: 'user' });
	};

	const isFileTooLarge = (file) => file && file.size > config.maxFileSize;

	const rejectOversizedFiles = (input) => {
		if (!input?.files?.length) return false;

		const oversized = [...input.files].filter(isFileTooLarge);
		if (!oversized.length) {
			// Vorherige Dateigrößen-Meldung zurücksetzen
			if (ui.status?.dataset.type === 'error'
				&& ui.status.textContent === (messages.fileTooLarge || 'File too large')) {
				setStatus('');
			}
			return false;
		}

		logSoftgardenDebug('file too large (client)', {
			limitBytes: config.maxFileSize,
			files: oversized.map((file) => ({ name: file.name, size: file.size })),
		});

		input.value = '';
		setStatus(messages.fileTooLarge || 'File too large', 'error', { focus: true });
		return true;
	};

	const validateFiles = (form) => {
		const cvInput = form.querySelector('#sg-cv');
		const attachmentsInput = form.querySelector('#sg-attachments');
		const cv = cvInput?.files?.[0];

		if (!cv) {
			throw new Error(messages.cvRequired || 'CV required');
		}

		if (isFileTooLarge(cv) || [...(attachmentsInput?.files || [])].some(isFileTooLarge)) {
			throw new Error(messages.fileTooLarge || 'File too large');
		}

		return { cv, extra: [...(attachmentsInput?.files || [])] };
	};

	const init = async () => {
		if (!api.clientId || !config.jobId) {
			ui.configError?.removeAttribute('hidden');
			return;
		}

		if (resetAuthToken) {
			showResetPassword();
			setStatus('');
		}

		setLoading(true, messages.loadingDefault);

		try {
			const questions = await api.get(`/api/rest/v3/frontend/jobs/${config.jobId}/questions`);
			renderQuestions(
				root.querySelector('[data-list="questions"]'),
				questions,
				ui.questionsFieldset,
			);

			if (!resetAuthToken) {
				showMainForm();
			}
		} catch (error) {
			setStatus(resolveUserMessage(error, messages, 'loadError'), 'error', { focus: true });
			debugFlowError('load', error);
			if (!resetAuthToken) {
				showMainForm();
			}
		} finally {
			setLoading(false);
		}
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		const form = event.currentTarget;

		if (!form.checkValidity()) {
			form.reportValidity();
			return;
		}

		setLoading(true, messages.loadingSubmit || messages.loadingDefault, { focus: true });
		setStatus('');

		let profile;
		let submitted = false;

		try {
			profile = collectProfile(form, root, config);
			const { cv, extra } = validateFiles(form);

			await authenticateApplicant(profile);

			const applicationId = await createDraft();
			await saveProfile(applicationId, profile);

			setLoading(true, messages.loadingUpload || messages.loadingSubmit, { focus: true });
			await uploadFile(api, applicationId, cv);
			for (const file of extra) {
				await uploadFile(api, applicationId, file);
			}

			setLoading(true, messages.loadingSubmit || messages.loadingDefault, { focus: true });
			await submitApplication(applicationId);
			submitted = true;
			showSuccess();
			form.reset();
		} catch (error) {
			debugFlowError('submit', error);

			// Softgarden kann die Bewerbung schon angenommen haben (Mail),
			// während Proxy/Timeout danach noch einen Fehler liefert.
			if (submitted) {
				showSuccess();
				form.reset();
				return;
			}

			const email = profile?.email || form.querySelector('#sg-email')?.value?.trim().toLowerCase() || '';

			if (error instanceof SoftgardenApiError && error.status === 401) {
				api.userToken = null;
				clearSession();
				if (email) {
					clearStoredCredential(email, credentialScope);
				}
			}
			if (isExistingAccountError(error)) {
				if (email && !(await tryAuthenticateWithStored(email))) {
					handleExistingAccount(email);
				}
			} else {
				setStatus(resolveUserMessage(error, messages, 'submitError'), 'error', { focus: true });
			}
		} finally {
			setLoading(false);
		}
	};

	const handleLogin = async (event) => {
		event.preventDefault();
		const loginForm = event.currentTarget;
		const email = loginForm.querySelector('[name="loginEmail"]')?.value.trim();
		const password = loginForm.querySelector('[name="loginPassword"]')?.value;

		if (!email || !password) return;

		setLoading(true);
		setStatus('');

		try {
			pendingApplicant = { email, username: email, password };
			await ensureUserToken({ username: email, password });
			writeStoredCredential(email, password, credentialScope);
			showMainForm();
			setStatus(messages.loginSuccess || '', 'success', { focus: true });
		} catch (error) {
			debugFlowError('login', error);
			setStatus(resolveUserMessage(error, messages, 'loginError'), 'error', { focus: true });
		} finally {
			setLoading(false);
		}
	};

	const handleLostPassword = async () => {
		// Softgarden API lostPasswordLink liefert für diesen Client aktuell immer invalid_url
		// (Return-URL-Whitelist). Daher natives Softgarden-Portal nutzen.
		const portalBase = root.dataset.passwordResetPortalUrl?.trim();
		if (portalBase) {
			try {
				const portalUrl = new URL(portalBase, window.location.origin);
				const sgLang = config.locale === 'nl' ? 'en' : (config.locale || 'de');
				if (!portalUrl.searchParams.has('l')) {
					portalUrl.searchParams.set('l', sgLang);
				}
				window.open(portalUrl.toString(), '_blank', 'noopener,noreferrer');
				setStatus(messages.passwordResetExternal || messages.passwordResetSent || '', 'success', { focus: true });
			} catch {
				setStatus(messages.passwordResetError, 'error', { focus: true });
			}
			return;
		}

		const email = root.querySelector('#sg-login-email')?.value.trim()
			|| root.querySelector('#sg-email')?.value.trim();

		if (!email) {
			setStatus(messages.emailRequired || 'Email required', 'error', { focus: true });
			return;
		}

		setLoading(true);

		try {
			const resetUrl = root.dataset.passwordResetUrl
				|| `${window.location.origin}${window.location.pathname}${window.location.search}`;

			await api.post('/api/rest/v2/frontend/lostPasswordLink', undefined, {
				query: {
					email,
					url: resetUrl,
				},
			});
			setStatus(messages.passwordResetSent || '', 'success', { focus: true });
		} catch (error) {
			debugFlowError('lostPassword', error);
			const parts = collectErrorParts(error);
			const isInvalidUrl = /invalid_url/i.test(parts);
			setStatus(
				isInvalidUrl
					? (messages.passwordResetInvalidUrl || messages.passwordResetError)
					: resolveUserMessage(error, messages, 'passwordResetError'),
				'error',
				{ focus: true },
			);
		} finally {
			setLoading(false);
		}
	};

	const handleResetPassword = async (event) => {
		event.preventDefault();
		const form = event.currentTarget;
		const password = form.querySelector('[name="resetPassword"]')?.value || '';
		const confirm = form.querySelector('[name="resetPasswordConfirm"]')?.value || '';

		if (!resetAuthToken) {
			setStatus(messages.resetTokenMissing, 'error', { focus: true });
			showExistingAccount();
			return;
		}

		if (password !== confirm) {
			setStatus(messages.resetMismatch, 'error', { focus: true });
			return;
		}

		const policy = validateSoftgardenPassword(password);
		if (!policy.ok) {
			setStatus(messages.resetInvalid, 'error', { focus: true });
			return;
		}

		setLoading(true);
		setStatus('');

		try {
			const result = await api.resetPassword(resetAuthToken, password);

			if (result === false) {
				throw new SoftgardenApiError(messages.resetTokenMissing, { status: 400 });
			}

			resetAuthToken = '';
			clearResetAuthTokenFromUrl();
			form.reset();
			showExistingAccount();
			setStatus(messages.resetSuccess, 'success', { focus: true });
		} catch (error) {
			debugFlowError('resetPassword', error);
			setStatus(resolveUserMessage(error, messages, 'passwordResetError'), 'error', { focus: true });
		} finally {
			setLoading(false);
		}
	};

	ui.main?.addEventListener('submit', handleSubmit);
	root.querySelector('.apply-form__login')?.addEventListener('submit', handleLogin);
	root.querySelector('.apply-form__reset')?.addEventListener('submit', handleResetPassword);
	root.querySelector('.js-sg-lost-password')?.addEventListener('click', handleLostPassword);

	['#sg-cv', '#sg-attachments'].forEach((selector) => {
		root.querySelector(selector)?.addEventListener('change', (event) => {
			rejectOversizedFiles(event.currentTarget);
		});
	});

	init();
};
